import LocalDataService from './LocalDataService';
import { toast } from 'react-toastify';
import { API_BASE_V2, API_BASE, ENDPOINTS } from '../config/api';

class VoiceService {
  constructor() {
    // 使用统一的 API 配置
    this.baseURL = API_BASE_V2;
  }

  /**
   * 获取用户token
   */
  getUserToken() {
    const userData = LocalDataService.load_user_data();
    if (!userData || !userData.token) {
      toast.error('请先登录');
      throw new Error('用户未登录');
    }
    return userData.token;
  }

  /**
   * 解析 output_params（可能是字符串或对象）
   */
  parseOutputParams(outputParams) {
    if (!outputParams) return null;
    if (typeof outputParams === 'string') {
      try {
        return JSON.parse(outputParams);
      } catch (e) {
        console.error('解析 output_params 失败:', e);
        return null;
      }
    }
    return outputParams;
  }

  /**
   * 解析 input_params（可能是字符串或对象）
   */
  parseInputParams(inputParams) {
    if (!inputParams) return null;
    if (typeof inputParams === 'string') {
      try {
        return JSON.parse(inputParams);
      } catch (e) {
        console.error('解析 input_params 失败:', e);
        return null;
      }
    }
    return inputParams;
  }

  /**
   * 通用任务创建方法
   */
  async createTask(appName, inputParams, taskType = 'minimax_api') {
    const token = this.getUserToken();

    try {
      const response = await fetch(`${this.baseURL}/tasks/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          user_type: 1,
          app_name: appName,
          task_type: taskType,
          input_params: inputParams
        })
      });

      const data = await response.json();

      if (data.code === 200 && data.data) {
        return data.data;
      } else {
        throw new Error(data.message || '任务创建失败');
      }
    } catch (error) {
      console.error('创建任务失败:', error);
      throw error;
    }
  }

  /**
   * 查询任务状态
   */
  async getTaskInfo(taskId) {
    try {
      const response = await fetch(`${this.baseURL}/tasks/info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ task_id: taskId })
      });

      const data = await response.json();

      if (data.code === 200 && data.data) {
        const taskData = data.data;
        
        // 解析 input_params 和 output_params（它们可能是 JSON 字符串）
        return {
          ...taskData,
          input_params: this.parseInputParams(taskData.input_params),
          output_params: this.parseOutputParams(taskData.output_params)
        };
      } else {
        throw new Error(data.message || '获取任务信息失败');
      }
    } catch (error) {
      console.error('查询任务状态失败:', error);
      throw error;
    }
  }

  /**
   * 轮询任务状态
   */
  async pollTaskStatus(taskId, options = {}) {
    const {
      maxAttempts = 60,
      onProgress = null,
      onCompleted = null,
      onFailed = null
    } = options;

    let attempts = 0;

    const poll = async () => {
      try {
        const taskInfo = await this.getTaskInfo(taskId);

        if (onProgress) {
          onProgress(taskInfo);
        }

        if (taskInfo.status === 'completed') {
          if (onCompleted) {
            onCompleted(taskInfo);
          }
          return taskInfo;
        } else if (taskInfo.status === 'failed') {
          if (onFailed) {
            onFailed(taskInfo);
          }
          throw new Error('任务执行失败');
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          throw new Error('任务超时');
        }
      } catch (error) {
        console.error('轮询任务状态失败:', error);
        throw error;
      }
    };

    return poll();
  }

  /**
   * 上传音频文件到 Minimax
   */
  async uploadAudioFile(file, purpose = 'voice_clone') {
    const token = this.getUserToken();

    // 验证文件
    if (!file) {
      throw new Error('请选择文件');
    }

    const validFormats = ['audio/mpeg', 'audio/mp3', 'audio/m4a', 'audio/wav', 'audio/x-m4a'];
    if (!validFormats.includes(file.type) && !file.name.match(/\.(mp3|m4a|wav)$/i)) {
      throw new Error('仅支持 MP3、M4A、WAV 格式的音频文件');
    }

    if (file.size > 20 * 1024 * 1024) {
      throw new Error('文件大小不能超过 20MB');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('purpose', purpose);
    formData.append('token', token);
    formData.append('user_type', '1');

    try {
      const response = await fetch(`${this.baseURL}/minimax/files/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      console.log('上传响应:', data);

      if (data.code === 200 && data.data) {
        return data.data.file_id;
      } else {
        throw new Error(data.msg || data.message || data.detail || JSON.stringify(data) || '上传失败');
      }
    } catch (error) {
      console.error('上传文件失败:', error);
      throw error;
    }
  }

  /**
   * 获取音色列表
   */
  async getVoiceList() {
    const token = this.getUserToken();

    try {
      const response = await fetch(`${this.baseURL}/minimax/voices?status=active`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          user_type: 1
        })
      });

      const data = await response.json();

      if (data.code === 200 && data.data) {
        const { public_voices, user_voices } = data.data;

        // 处理公共音色（系统音色）
        const publicVoiceList = (public_voices || []).map(voice => {
          // 根据音色ID判断语言和性别
          const voiceId = voice.voice_id.toLowerCase();
          let language = 'zh';  // 默认中文
          let gender = 'male';  // 默认男声

          // 判断语言
          if (voiceId.includes('english') || voiceId.includes('_en_')) {
            language = 'en';
          }

          // 判断性别
          if (voiceId.includes('female') || voiceId.includes('lady') ||
              voiceId.includes('shaonv') || voiceId.includes('yujie') ||
              voiceId.includes('chengshu') || voiceId.includes('tianmei')) {
            gender = 'female';
          }

          // 从描述数组中提取第一个描述作为简短描述
          const description = Array.isArray(voice.description) && voice.description.length > 0
            ? voice.description[0]
            : '';

          return {
            id: voice.voice_id,
            name: voice.voice_name,
            gender: gender,
            language: language,
            description: description,
            tags: voice.tags || [],
            audio_url: voice.audio_url || '',
            is_public: true,
            voice_type: 'system'
          };
        });

        // 处理用户音色
        const userVoiceList = (user_voices || []).map(voice => {
          // 用户音色默认为中文，性别根据 voice_type 或其他信息判断
          return {
            id: voice.voice_id,
            name: voice.voice_name,
            gender: 'male',  // 用户音色暂时默认为男声，可以后续优化
            language: 'zh',
            description: voice.prompt_text || voice.demo_text || '自定义音色',
            audio_url: voice.demo_audio_url || '',  // 使用 demo_audio_url 作为试听音频
            is_public: false,
            voice_type: voice.voice_type,
            voice_type_display: voice.voice_type_display,
            usage_count: voice.usage_count || 0,
            created_at: voice.created_at,
            is_expired: voice.status !== 'active',
            expires_at: voice.expires_at
          };
        });

        // 合并公共音色和用户音色
        return [...publicVoiceList, ...userVoiceList];
      } else {
        throw new Error(data.message || '获取音色列表失败');
      }
    } catch (error) {
      console.error('获取音色列表失败:', error);
      throw error;
    }
  }

  /**
   * 创建声音克隆任务
   */
  async createVoiceClone(inputParams) {
    return this.createTask('minimax/voice-clone', inputParams);
  }

  /**
   * 创建设音设计任务
   */
  async createVoiceDesign(inputParams) {
    return this.createTask('minimax/voice-design', inputParams);
  }

  /**
   * 创建语音合成任务
   */
  async createVoiceSynthesis(inputParams) {
    return this.createTask('minimax/t2a', inputParams);
  }

  /**
   * 轮询声音克隆任务状态
   */
  async pollVoiceCloneTask(taskId, callbacks = {}) {
    return this.pollTaskStatus(taskId, {
      onProgress: callbacks.onProgress,
      onCompleted: (taskInfo) => {
        const outputParams = this.parseOutputParams(taskInfo.output_params);
        const inputParams = this.parseInputParams(taskInfo.input_params);

        const result = {
          taskInfo,
          audioUrl: outputParams?.trial_audio || outputParams?.demo_audio,
          voiceId: inputParams?.voice_id || outputParams?.voice_id,
          outputParams,
          inputParams
        };

        if (callbacks.onCompleted) {
          callbacks.onCompleted(result);
        }
      },
      onFailed: callbacks.onFailed
    });
  }

  /**
   * 轮询声音设计任务状态
   */
  async pollVoiceDesignTask(taskId, callbacks = {}) {
    return this.pollTaskStatus(taskId, {
      onProgress: callbacks.onProgress,
      onCompleted: (taskInfo) => {
        const outputParams = this.parseOutputParams(taskInfo.output_params);

        const result = {
          taskInfo,
          audioUrl: outputParams?.trial_audio || outputParams?.demo_audio,
          voiceId: outputParams?.voice_id,
          outputParams
        };

        if (callbacks.onCompleted) {
          callbacks.onCompleted(result);
        }
      },
      onFailed: callbacks.onFailed
    });
  }

  /**
   * 轮询语音合成任务状态
   */
  async pollVoiceSynthesisTask(taskId, callbacks = {}) {
    return this.pollTaskStatus(taskId, {
      onProgress: callbacks.onProgress,
      onCompleted: (taskInfo) => {
        const outputParams = this.parseOutputParams(taskInfo.output_params);

        const result = {
          taskInfo,
          audioUrl: outputParams?.data?.audio || outputParams?.audio,
          outputParams
        };

        if (callbacks.onCompleted) {
          callbacks.onCompleted(result);
        }
      },
      onFailed: callbacks.onFailed
    });
  }

  /**
   * 更新音色信息（名称和描述）
   * @param {string} voiceId - 音色ID
   * @param {object} updateData - 更新数据 { voice_name, description }
   */
  async updateVoice(voiceId, updateData) {
    const token = this.getUserToken();

    try {
      const response = await fetch(ENDPOINTS.MINIMAX_VOICES_UPDATE, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_type : 1,
          token: token,
          voice_id: voiceId,
          ...updateData
        })
      });

      const data = await response.json();

      if (data.code === 200 && data.data) {
        return data.data;
      } else {
        throw new Error(data.detail || data.message || '更新音色失败');
      }
    } catch (error) {
      console.error('更新音色失败:', error);
      throw error;
    }
  }

  /**
   * 获取用户音色列表（仅用户自己的音色）
   */
  async getUserVoiceList() {
    const token = this.getUserToken();

    try {
      const response = await fetch(`${this.baseURL}/minimax/voices?status=active`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          user_type: 1
        })
      });

      const data = await response.json();

      if (data.code === 200 && data.data) {
        const { user_voices } = data.data;

        // 处理用户音色
        return (user_voices || []).map(voice => ({
          id: voice.voice_id,
          name: voice.voice_name,
          description: voice.description || voice.prompt_text || voice.demo_text || '',
          audio_url: voice.demo_audio_url || '',
          voice_type: voice.voice_type,
          voice_type_display: voice.voice_type_display,
          usage_count: voice.usage_count || 0,
          created_at: voice.created_at,
          is_expired: voice.is_expired || false,
          expires_at: voice.expires_at
        }));
      } else {
        throw new Error(data.message || '获取音色列表失败');
      }
    } catch (error) {
      console.error('获取用户音色列表失败:', error);
      throw error;
    }
  }

  /**
   * 获取语音合成任务列表
   */
  async getVoiceSynthesisTaskList(page = 1, pageSize = 20) {
    const token = this.getUserToken();

    try {
      // 使用正确的 API 路径，根据 curl 请求，应该是 /api/fal/tasks/list
      const apiBase = API_BASE;
      const response = await fetch(`${apiBase}/fal/tasks/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          user_type: 1,
          page: page,
          page_size: pageSize,
          app_name: 'minimax/t2a' // 只获取语音合成任务
        })
      });

      const data = await response.json();

      if (data.code === 200 && data.data) {
        const taskList = data.data.list || [];
        
        // 处理任务列表，解析 input_params 和 output_params
        return taskList.map(task => ({
          ...task,
          input_params: this.parseInputParams(task.input_params),
          output_params: this.parseOutputParams(task.output_params)
        }));
      } else {
        throw new Error(data.message || data.msg || '获取任务列表失败');
      }
    } catch (error) {
      console.error('获取任务列表失败:', error);
      throw error;
    }
  }
}

export default new VoiceService();
